/** @jsx jsx */
/* eslint-disable max-len */
import React, { useState } from 'react';
import { css, jsx } from '@emotion/core';
import Modal from 'components/molecules/Modal';
import Icon from 'components/atoms/Icon';
import { autoGenerate } from 'api/shiftBoard';
import { GeneratedOperations, StaffInfo } from 'domain/attend/shiftBoard/types';
import { shortDateLabel } from 'domain/attend/shiftBoard/time';
import { boardStyles, mock } from './styles';

export type AutoGenerateModalProps = {
  open: boolean;
  orgCode: string;
  dateFrom: string;
  dateTo: string;
  staffs: Array<StaffInfo>;
  onClose: () => void;
  onGenerated: (result: GeneratedOperations) => void;
};

const SKIP_LABEL: { [k: string]: string } = {
  NO_WEEK_PATTERN: '曜日設定なし',
  HOPE_OFF: '希望が休み',
  SHIFT_EXISTS: '登録済み',
  AWAY_SHIFT: '他店支援',
  LOCKED: 'ロック中',
  NOT_EMPLOYED: '在籍外',
};

const styles = {
  lead: css({
    fontSize: '12px', color: mock.textMain, lineHeight: 1.8, margin: '0 0 12px',
  }),
  guideToggle: css({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    background: 'none',
    border: 'none',
    padding: 0,
    color: mock.primary,
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    marginBottom: '8px',
  }),
  guide: css({
    border: `1px solid ${mock.gray10}`,
    borderRadius: '4px',
    padding: '12px 14px',
    marginBottom: '16px',
    fontSize: '12px',
    lineHeight: 1.8,
    color: mock.textMain,
  }),
  guideHeading: css({ fontWeight: 700, margin: '0 0 4px' }),
  guideList: css({
    margin: '0 0 10px', padding: 0, listStyle: 'none',
  }),
  guideItem: css({
    display: 'flex', gap: '8px', padding: '2px 0',
  }),
  guideTerm: css({
    flex: 'none', width: '104px', color: mock.textSub,
  }),
};

/**
 * スタッフの曜日設定から自動作成（結果をシフト表に取り込む。取り込んだ時点で保存される。9/24〜）
 *
 * 元になるのはスタッフマスタの「シフトパターン設定」タブ（曜日ごとの割り当て。staff_week_shift_pattern）。
 * API・テーブル側は週パターンという言い方のままなので、画面の言葉とは分けて読むこと。
 *
 * 何をする機能か分かりにくいという指摘（9/18）を受けて、前期間からコピーと同じように
 * 開閉できる説明を付けている。
 */
const AutoGenerateModal: React.FC<AutoGenerateModalProps> = ({
  open, orgCode, dateFrom, dateTo, staffs, onClose, onGenerated,
}) => {
  const [selected, setSelected] = useState<Array<string>>([]);
  const [overwrite, setOverwrite] = useState(false);
  const [respectHope, setRespectHope] = useState(true);
  const [result, setResult] = useState<GeneratedOperations | null>(null);
  const [guideOpen, setGuideOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const candidates = staffs.filter((s) => s.hasWeekPattern && !s.isGuest);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await autoGenerate({
        orgCode, dateFrom, dateTo, staffCodes: selected, overwrite, respectHope,
      });
      setResult(r);
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.message) || '作成できませんでした。');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      closeHandler={() => { setResult(null); onClose(); }}
      title="スタッフの曜日設定から自動作成"
      submitText={result ? 'シフト表に取り込む' : '作成'}
      onSubmit={() => {
        if (result) {
          onGenerated(result);
          setResult(null);
        } else {
          run();
        }
      }}
      // 作るものが無いときは送信だけ止める（キャンセルは押せる）。通信中はキャンセルも止める（10/5）
      isDisabledAll={busy}
      isDisabledSubmit={!!result && result.generated === 0}
      width="640px"
    >
      <div css={boardStyles.modalBody}>
        <div css={styles.lead}>
          {`${shortDateLabel(dateFrom)} 〜 ${shortDateLabel(dateTo)} のシフト案を、スタッフごとの曜日設定から作ります。`}
        </div>

        <button type="button" css={styles.guideToggle} onClick={() => setGuideOpen(!guideOpen)} aria-expanded={guideOpen}>
          <span>この機能の説明</span>
          <Icon type={guideOpen ? 'chevronUp' : 'chevronDown'} color={mock.primary} size="16px" verticalAlign="middle" />
        </button>
        {guideOpen && (
          <div css={styles.guide}>
            <p css={styles.guideHeading}>どこから作るか</p>
            <div style={{ marginBottom: '10px' }}>
              スタッフマスタの「シフトパターン設定」で曜日ごとに登録したパターンを、指定した期間の各日に当てはめます。
              祝日は祝日用の枠が優先されます。過去の実績ではなく、スタッフに紐づく定型の設定が元になります。
            </div>
            <p css={styles.guideHeading}>作られない日</p>
            <ul css={styles.guideList}>
              <li css={styles.guideItem}>
                <span css={styles.guideTerm}>曜日設定なし</span>
                <span>その曜日に設定がないスタッフ・日は作りません。</span>
              </li>
              <li css={styles.guideItem}>
                <span css={styles.guideTerm}>希望が休み</span>
                <span>休み希望の日は空けます。下の「希望（休み）を優先する」を外すと無視して埋めます。</span>
              </li>
              <li css={styles.guideItem}>
                <span css={styles.guideTerm}>登録済み</span>
                <span>すでにシフトがある日は触りません。「登録済みのシフトを上書きする」を入れると置き換えます。</span>
              </li>
              <li css={styles.guideItem}>
                <span css={styles.guideTerm}>他店支援</span>
                <span>他店へ支援に出ている日は作りません。</span>
              </li>
            </ul>
            <p css={styles.guideHeading}>保存のしかた</p>
            <div>
              「作成」を押した時点では保存されません。内容を確かめて「シフト表に取り込む」を押すと、
              その場で保存されます。
            </div>
          </div>
        )}
        {!result && (
          <React.Fragment>
            <div css={boardStyles.formRow}>
              <label htmlFor="ag-overwrite">
                <input id="ag-overwrite" type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
                {' '}
                登録済みのシフトを上書きする
              </label>
              <label htmlFor="ag-hope">
                <input id="ag-hope" type="checkbox" checked={respectHope} onChange={(e) => setRespectHope(e.target.checked)} />
                {' '}
                希望（休み）を優先する
              </label>
            </div>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>{`対象スタッフ（未選択なら曜日設定のある全員 ${candidates.length} 人）`}</div>
            <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
              {candidates.map((s) => (
                <label key={s.staffCode} htmlFor={`ag-${s.staffCode}`} css={boardStyles.listRow} style={{ minHeight: '36px' }}>
                  <input
                    id={`ag-${s.staffCode}`}
                    type="checkbox"
                    checked={selected.indexOf(s.staffCode) !== -1}
                    onChange={(e) => setSelected(e.target.checked ? [...selected, s.staffCode] : selected.filter((c) => c !== s.staffCode))}
                  />
                  <span>{s.staffName}</span>
                  <span css={boardStyles.subText}>{s.employmentName || ''}</span>
                </label>
              ))}
            </div>
          </React.Fragment>
        )}
        {error && <div css={boardStyles.dangerText}>{error}</div>}
        {result && (
          <React.Fragment>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>{`作る ${result.generated} 件／作らない ${result.skipped.length} 件`}</div>
            {result.skipped.length > 0 && (
              <div css={boardStyles.subText} style={{ marginBottom: '6px' }}>作らない日と、その理由です。</div>
            )}
            <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
              {result.skipped.slice(0, 200).map((s) => (
                <div key={`${s.staffCode}-${s.targetDate}`} css={boardStyles.subText}>
                  {`${shortDateLabel(s.targetDate)} ${(staffs.filter((x) => x.staffCode === s.staffCode)[0] || { staffName: s.staffCode }).staffName}: ${SKIP_LABEL[s.reason] || s.reason}`}
                </div>
              ))}
            </div>
          </React.Fragment>
        )}
      </div>
    </Modal>
  );
};

export default AutoGenerateModal;
